import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Archive, Ellipsis, Package, Pencil, Search } from 'lucide-react'
import { useState } from 'react'
import { productsQuery, type Product } from '@/api/goods'
import { GoodsTabs } from '@/app/goods/goods-shell'
import { RetireDialog } from '@/app/goods/retire-dialog'
import { scanTone } from '@/app/goods/scan-sound'
import { ShelfFilter } from '@/app/goods/shelf'
import { useBarcodeScanner } from '@/app/goods/use-barcode-scanner'
import { useScanLookup } from '@/app/goods/use-scan-lookup'
import { useShelfFilter } from '@/app/goods/use-shelf'
import { useGoodsActions } from '@/app/goods/use-goods-actions'
import { NothingForRole } from '@/app/nothing-for-role'
import { StateFilter } from '@/ui/state-filter'
import { canonicalBarcode, checkDigit } from '@/domain/barcode'
import { errorText } from '@/domain/errors'
import { goodsErrorText } from '@/domain/goods-errors'
import {
  countByFilter,
  matchesFilter,
  matchesSearch,
  STOCK_FILTERS,
  stockState,
  stockValue,
  type StockFilter,
} from '@/domain/goods'
import { formatMoney } from '@/domain/pass-rules'
import { scanSay, type ScanNews } from '@/domain/scan-news'
import { clubSearch, shelfFromKey, shelfKey } from '@/domain/shelves'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { withKey } from '@/lib/search'
import { serially } from '@/lib/serially'
import { DataList, type DataColumn } from '@/ui/data-list'
import { Field } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { RowMenu, RowMenuItem } from '@/ui/overlays'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile, StatTiles } from '@/ui/stat-tile'

/// `club` — the shelf the list is counted on (domain/shelves.ts `shelfKey`),
/// in the URL so a reload, a link and «Назад» keep it.
type StockSearch = { filter?: StockFilter; club?: string }

export const Route = createFileRoute('/_app/c/$orgId/goods/')({
  validateSearch: (raw: Record<string, unknown>): StockSearch => ({
    ...(typeof raw['filter'] === 'string' && (STOCK_FILTERS as readonly string[]).includes(raw['filter']) && raw['filter'] !== 'all'
      ? { filter: raw['filter'] as StockFilter }
      : {}),
    ...clubSearch(raw['club']),
  }),
  component: Stock,
})

const s = () => t.goods.stock

const STATE_DOT: Record<string, string> = {
  ok: 'bg-success',
  low: 'bg-warning',
  out: 'bg-danger',
  retired: 'bg-[color-mix(in_srgb,var(--muted)_60%,transparent)]',
}

/// «Залишки»: every product with what is on the shelf now — counted by the
/// server from the papers, for the clubs the caller sees (or the one picked).
/// What runs low is a pill away; the shelf's worth at cost sits on top.
function Stock() {
  const { orgId } = Route.useParams()
  const { filter = 'all', club } = Route.useSearch()
  const navigate = Route.useNavigate()
  const shelf = shelfFromKey(club)
  const goods = useGoodsActions(orgId, { shelf })
  const { can } = goods
  const shelves = useShelfFilter(orgId, goods.access, can.see)
  // Another club keeps the list on screen, dimmed, until its figures arrive.
  const products = useQuery({ ...productsQuery(orgId, shelf), enabled: can.see, placeholderData: keepPreviousData })
  const [query, setQuery] = useState('')
  const [retiring, setRetiring] = useState<Product | null>(null)
  // A scan on the list opens the product's card — found in the list, or
  // asked of the server while it loads or when another desk added it a
  // minute ago. What did not open a card stays in a banner of its own until
  // put away; a code nobody has offers «Створити товар». With a panel or a
  // dialog open, that one has the scanner.
  const lookup = useScanLookup(orgId, shelf, products.data ?? [])
  const [scans] = useState(serially)
  const [miss, setMiss] = useState<ScanNews | null>(null)
  useBarcodeScanner(can.see && goods.panel === null && retiring === null, (outcome) => {
    void scans(async () => {
      const code = outcome.kind === 'code' ? canonicalBarcode(outcome.code) : null
      const found = code === null ? null : await lookup.find(code, () => undefined)
      const news: ScanNews | null =
        code === null || found === null
          ? { kind: 'unreadable' }
          : found.kind === 'product'
            ? null
            : found.kind === 'unknown'
              ? checkDigit(code) === 'bad'
                ? { kind: 'misread', source: 'scan' }
                : { kind: 'unknown', code }
              : { kind: found.kind }
      setMiss(news)
      scanTone(news === null)
      if (found?.kind === 'product') {
        await navigate({ to: '/c/$orgId/goods/$productId', params: { orgId, productId: found.product.id }, ...(club ? { search: { club } } : {}) })
      }
    })
  })
  const missSaid = miss ? scanSay(miss, { figureOf: () => null, canCreate: can.catalog }) : null

  if (!can.see) return <NothingForRole />

  const all = products.data ?? []
  const counts = countByFilter(all)
  const rows = all.filter((p) => matchesFilter(p, filter) && matchesSearch(p, query))
  const worth = stockValue(all)

  const columns: readonly DataColumn<Product>[] = [
    {
      key: 'name',
      header: s().columns.name,
      width: 'minmax(0,2fr)',
      cell: (p) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-text">{p.name}</span>
          <span className="truncate text-caption text-text-2">{p.category}</span>
        </span>
      ),
    },
    {
      key: 'onHand',
      header: s().columns.onHand,
      width: 'minmax(120px,0.9fr)',
      numeric: true,
      mobile: 'trailing',
      // A count among counts: right-aligned like every number. The state is
      // a word under it as well as a colour — «закінчується», «немає» —
      // never a dot alone.
      cell: (p) => {
        const state = stockState(p)
        return (
          <span className="flex flex-col items-end tabular">
            <span className="inline-flex items-center gap-2">
              <span aria-hidden="true" className={cn('size-2 shrink-0 rounded-full', STATE_DOT[state])} />
              <span className={cn('font-semibold', state === 'out' ? 'text-danger' : state === 'low' ? 'text-warning' : 'text-text')}>
                {p.onHand} {p.unit}
              </span>
            </span>
            {state === 'low' || state === 'out' ? (
              <span className={cn('text-caption', state === 'out' ? 'text-danger' : 'text-warning')}>{s().state[state]}</span>
            ) : (
              <span className="sr-only">{s().state[state]}</span>
            )}
          </span>
        )
      },
    },
    {
      key: 'min',
      header: s().columns.min,
      width: 'minmax(80px,0.6fr)',
      numeric: true,
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (p) => (p.minStock == null ? <span className="text-muted">{s().noMin}</span> : `${p.minStock} ${p.unit}`),
    },
    {
      key: 'price',
      header: s().columns.price,
      width: 'minmax(96px,0.8fr)',
      numeric: true,
      mobile: 'subtitle',
      cell: (p) => formatMoney(p.priceMinor),
    },
    // What the goods cost is `stock ≥ view` (specs/shop.md §4); a desk that
    // only sells reads the shelf and the prices.
    ...(can.costs
      ? [
          {
            key: 'cost',
            header: s().columns.cost,
            // Wide enough for its honest name.
            width: 'minmax(136px,0.9fr)',
            numeric: true,
            hideBelow: 'md',
            mobile: 'hidden',
            cell: (p: Product) =>
              p.lastCostMinor == null ? <span className="text-muted">{s().noCost}</span> : formatMoney(p.lastCostMinor),
          } satisfies DataColumn<Product>,
          {
            key: 'value',
            header: s().columns.value,
            width: 'minmax(110px,0.9fr)',
            numeric: true,
            hideBelow: 'lg',
            mobile: 'hidden',
            // «—» only for «unknown» (no purchase price); an empty shelf is 0 ₴.
            cell: (p: Product) =>
              p.lastCostMinor == null ? (
                <span className="text-muted">{s().noCost}</span>
              ) : (
                formatMoney(Math.max(p.onHand, 0) * p.lastCostMinor)
              ),
          } satisfies DataColumn<Product>,
        ]
      : []),
  ]

  return (
    <Page wide>
      <PageHeader title={t.goods.title} subtitle={t.goods.subtitle} actions={goods.actionsFor()} tabs={<GoodsTabs orgId={orgId} can={can} />} />

      {goods.news ? <InlineBanner kind="done" className="mb-6" message={goods.news} /> : null}
      {miss && missSaid ? (
        <InlineBanner
          kind={missSaid.tone === 'note' ? 'info' : miss.kind === 'offline' || miss.kind === 'forbidden' || miss.kind === 'failed' ? 'error' : 'warning'}
          className="mb-6"
          message={missSaid.text}
          action={
            missSaid.create
              ? {
                  label: t.goods.scan.createProduct,
                  onClick: () => {
                    setMiss(null)
                    goods.createProduct(missSaid.create as string)
                  },
                }
              : undefined
          }
          onDismiss={() => setMiss(null)}
        />
      ) : null}

      {products.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : products.isError ? (
        <InlineBanner kind="error" message={errorText(products.error, goodsErrorText)} action={{ label: t.app.retry, onClick: () => void products.refetch() }} />
      ) : all.length === 0 ? (
        <EmptyState icon={<Package />} title={s().empty} message={can.catalog ? s().emptyMessage : s().emptyReadOnly} action={goods.firstStep} />
      ) : (
        <div className={cn('flex flex-col gap-5 transition-opacity', products.isPlaceholderData && 'opacity-60')}>
          {/* One compact strip — the list is what the page is for, and it
              starts right under it. On a phone the two counts share a row
              and the money figure takes the next one whole. */}
          <StatTiles className={can.costs ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'}>
            <StatTile compact label={s().summary.products} value={String(counts.all)} />
            <StatTile
              compact
              label={s().summary.low}
              value={String(counts.low)}
              tone={counts.low > 0 ? 'warning' : 'normal'}
              // The figure opens what it counts.
              onOpen={() => void navigate({ search: (prev) => withKey(prev, 'filter', 'low'), replace: true })}
              openLabel={s().summary.openLow(counts.low)}
            />
            {can.costs ? (
              <StatTile
                compact
                className="col-span-2 sm:col-span-1"
                label={s().summary.value}
                value={s().summary.approx(formatMoney(worth.minor))}
                note={
                  <span>
                    {[
                      s().summary.valueNote,
                      worth.unpriced > 0 ? s().summary.valueUnpriced(worth.unpriced) : null,
                      worth.retiredWithStock > 0 ? s().summary.retiredWithStock(worth.retiredWithStock) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                }
              />
            ) : null}
          </StatTiles>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <Field
                hideLabel
                label={s().search}
                placeholder={s().search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                trailing={<Search aria-hidden="true" className="mr-4 size-4 text-muted" />}
              />
            </div>
            <ShelfFilter
              filter={shelves}
              value={shelf}
              onChange={(next) =>
                void navigate({ search: (prev) => withKey(prev, 'club', next.kind === 'all' ? undefined : shelfKey(next)), replace: true })
              }
            />
          </div>

          <StateFilter
            label={s().filterLabel}
            value={filter}
            onChange={(next) => void navigate({ search: (prev) => withKey(prev, 'filter', next === 'all' ? undefined : next), replace: true })}
            options={STOCK_FILTERS.map((f) => ({ value: f, label: s().filters[f] ?? f, count: counts[f] }))}
          />

          <DataList
            label={t.goods.title}
            columns={columns}
            rows={rows}
            rowKey={(p) => p.id}
            rowTarget={(p) => ({
              to: '/c/$orgId/goods/$productId',
              params: { orgId, productId: p.id },
              ...(club ? { search: { club } } : {}),
            })}
            rowTone={(p) => (p.status === 'retired' ? 'muted' : 'normal')}
            rowActions={
              can.catalog
                ? (p) => (
                    <RowMenu label={p.name} trigger={<Ellipsis className="size-4" />}>
                      <RowMenuItem icon={<Pencil />} onSelect={() => goods.editProduct(p)}>
                        {t.goods.actions.edit}
                      </RowMenuItem>
                      {p.status === 'active' ? (
                        <RowMenuItem icon={<Archive />} onSelect={() => setRetiring(p)}>
                          {t.goods.actions.retire}
                        </RowMenuItem>
                      ) : null}
                    </RowMenu>
                  )
                : undefined
            }
            empty={s().emptyFilter}
          />
        </div>
      )}

      {goods.panel}

      {retiring ? (
        <RetireDialog
          orgId={orgId}
          product={retiring}
          seesEveryShelf={shelf.kind === 'all'}
          onClose={() => setRetiring(null)}
          onRetired={(product) => {
            setRetiring(null)
            goods.setNews(t.goods.product.retired(product.name))
          }}
          onWriteOff={(product) => {
            setRetiring(null)
            goods.startWriteOff(product)
          }}
        />
      ) : null}
    </Page>
  )
}
